import React, { useState, useEffect } from "react"
import { motion } from "framer-motion"
import {Search, Edit2, Trash2, Eye, FileText, CheckCircle, XCircle, Clock, Ban} from 'lucide-react'
import { useGuias } from "../context/GuiasContext"

interface ListarBoletosProps {
  onEdit?: (boletoId: string) => void
}

export default function ListarBoletos({ onEdit }: ListarBoletosProps) {
  const { boletos, deleteBoleto, cancelarBoleto, marcarBoletoComoPago } = useGuias()
  const [searchTerm, setSearchTerm] = useState("")
  const [filteredBoletos, setFilteredBoletos] = useState(boletos)
  const [selectedBoleto, setSelectedBoleto] = useState<any>(null)

  useEffect(() => {
    const filtered = boletos.filter(boleto =>
      boleto.nomePagador?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      boleto.cpfCnpjPagador?.includes(searchTerm) ||
      boleto.numeroBoleto?.includes(searchTerm) ||
      boleto.nossoNumero?.includes(searchTerm)
    )
    setFilteredBoletos(filtered)
  }, [searchTerm, boletos])

  const handleDelete = async (id: string) => {
    if (window.confirm("Deseja realmente excluir este boleto?")) {
      await deleteBoleto(id)
    }
  }

  const handleCancelar = async (id: string) => {
    if (window.confirm("Deseja cancelar este boleto?")) {
      await cancelarBoleto(id)
    }
  }

  const handleMarcarPago = async (id: string) => {
    const valor = prompt("Confirme o valor pago (R$):")
    if (valor) {
      await marcarBoletoComoPago(id, valor)
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pago":
        return (
          <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold flex items-center gap-1">
            <CheckCircle className="w-3 h-3" />
            Pago
          </span>
        )
      case "pendente":
        return (
          <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-semibold flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Pendente
          </span>
        )
      case "vencido":
        return (
          <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold flex items-center gap-1">
            <XCircle className="w-3 h-3" />
            Vencido
          </span>
        )
      case "cancelado":
        return (
          <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-semibold flex items-center gap-1">
            <Ban className="w-3 h-3" />
            Cancelado
          </span>
        )
      default:
        return null
    }
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return "-"
    return new Date(dateString).toLocaleDateString("pt-BR")
  }

  const formatCurrency = (value: string) => {
    if (!value) return "R$ 0,00"
    const num = parseFloat(value.replace(",", "."))
    return `R$ ${num.toFixed(2).replace(".", ",")}`
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-lg p-8"
    >
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <FileText className="w-6 h-6 text-[#004161]" />
          Boletos Cadastrados
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Total: {filteredBoletos.length} boleto(s)
        </p>
      </div>

      {/* Barra de Pesquisa */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Buscar por nome, CPF/CNPJ, número do boleto..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
          />
        </div>
      </div>

      {/* Tabela */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gradient-to-r from-[#004161] to-[#005580] text-white">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold">Nº Boleto</th>
              <th className="px-4 py-3 text-left text-sm font-semibold">Pagador</th>
              <th className="px-4 py-3 text-left text-sm font-semibold">CPF/CNPJ</th>
              <th className="px-4 py-3 text-left text-sm font-semibold">Valor</th>
              <th className="px-4 py-3 text-left text-sm font-semibold">Vencimento</th>
              <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
              <th className="px-4 py-3 text-center text-sm font-semibold">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredBoletos.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                  Nenhum boleto encontrado
                </td>
              </tr>
            ) : (
              filteredBoletos.map((boleto) => (
                <tr key={boleto._id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    {boleto.numeroBoleto || "-"}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {boleto.nomePagador}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {boleto.cpfCnpjPagador}
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-green-600">
                    {formatCurrency(boleto.valor)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {formatDate(boleto.dataVencimento)}
                  </td>
                  <td className="px-4 py-3">
                    {getStatusBadge(boleto.statusBoleto)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-2">
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => setSelectedBoleto(boleto)}
                        className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
                        title="Visualizar"
                      >
                        <Eye className="w-4 h-4" />
                      </motion.button>
                      
                      {boleto.statusBoleto === "pendente" && (
                        <>
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => onEdit && onEdit(boleto._id!)}
                            className="p-2 bg-amber-100 text-amber-600 rounded-lg hover:bg-amber-200 transition-colors"
                            title="Editar"
                          >
                            <Edit2 className="w-4 h-4" />
                          </motion.button>
                          
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => handleMarcarPago(boleto._id!)}
                            className="p-2 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors"
                            title="Marcar como Pago"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </motion.button>
                          
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => handleCancelar(boleto._id!)}
                            className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                            title="Cancelar"
                          >
                            <Ban className="w-4 h-4" />
                          </motion.button>
                        </>
                      )}
                      
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => handleDelete(boleto._id!)}
                        className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                        title="Excluir"
                      >
                        <Trash2 className="w-4 h-4" />
                      </motion.button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal de Visualização */}
      {selectedBoleto && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-800">Detalhes do Boleto</h3>
                <button
                  onClick={() => setSelectedBoleto(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <XCircle className="w-6 h-6 text-gray-500" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Número do Boleto</p>
                    <p className="font-semibold text-gray-800">{selectedBoleto.numeroBoleto || "-"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Status</p>
                    <div className="mt-1">{getStatusBadge(selectedBoleto.statusBoleto)}</div>
                  </div>
                </div>

                <hr />

                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">Pagador</h4>
                  <div className="space-y-2">
                    <p><span className="text-gray-500">Nome:</span> {selectedBoleto.nomePagador}</p>
                    <p><span className="text-gray-500">CPF/CNPJ:</span> {selectedBoleto.cpfCnpjPagador}</p>
                    {selectedBoleto.enderecoPagador && (
                      <p><span className="text-gray-500">Endereço:</span> {selectedBoleto.enderecoPagador}</p>
                    )}
                  </div>
                </div>

                <hr />

                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">Dados Financeiros</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Valor</p>
                      <p className="font-semibold text-green-600 text-lg">{formatCurrency(selectedBoleto.valor)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Data de Vencimento</p>
                      <p className="font-semibold text-gray-800">{formatDate(selectedBoleto.dataVencimento)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Data de Emissão</p>
                      <p className="font-semibold text-gray-800">{formatDate(selectedBoleto.dataEmissao)}</p>
                    </div>
                    {selectedBoleto.dataPagamento && (
                      <div>
                        <p className="text-sm text-gray-500">Data de Pagamento</p>
                        <p className="font-semibold text-gray-800">{formatDate(selectedBoleto.dataPagamento)}</p>
                      </div>
                    )}
                  </div>
                </div>

                {selectedBoleto.linhaDigitavel && (
                  <>
                    <hr />
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Linha Digitável</p>
                      <p className="font-mono text-sm bg-gray-100 p-2 rounded">{selectedBoleto.linhaDigitavel}</p>
                    </div>
                  </>
                )}

                {selectedBoleto.nossoNumero && (
                  <div>
                    <p className="text-sm text-gray-500">Nosso Número</p>
                    <p className="font-semibold text-gray-800">{selectedBoleto.nossoNumero}</p>
                  </div>
                )}

                {selectedBoleto.descricao && (
                  <>
                    <hr />
                    <div>
                      <p className="text-sm text-gray-500">Descrição</p>
                      <p className="text-gray-800">{selectedBoleto.descricao}</p>
                    </div>
                  </>
                )}

                {(selectedBoleto.instrucoes || selectedBoleto.observacoes) && (
                  <>
                    <hr />
                    {selectedBoleto.instrucoes && (
                      <div>
                        <p className="text-sm text-gray-500">Instruções</p>
                        <p className="text-gray-800">{selectedBoleto.instrucoes}</p>
                      </div>
                    )}
                    {selectedBoleto.observacoes && (
                      <div>
                        <p className="text-sm text-gray-500">Observações</p>
                        <p className="text-gray-800">{selectedBoleto.observacoes}</p>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="mt-6">
                <button
                  onClick={() => setSelectedBoleto(null)}
                  className="w-full py-2 bg-gradient-to-r from-[#004161] to-[#005580] text-white rounded-lg font-semibold hover:shadow-lg transition-shadow"
                >
                  Fechar
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </motion.div>
  )
}
